import { useState, useEffect } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Webhook,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsField } from "@/components/v2/settings-field";
import { SettingsTable } from "@/components/v2/settings-table";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  useWebhookEndpoints,
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useWebhookPortalUrl,
  type WebhookEndpoint,
} from "@/hooks/use-webhook-endpoints";
import {
  WEBHOOK_ENTITY_TYPES,
  WEBHOOK_SYSTEM_EVENT_TYPES,
  ENTITY_EVENT_OPS,
} from "@adva/types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ENTITY_LABELS: Record<string, string> = {
  customer: "Customer",
  contact: "Contact",
  team_member: "Team Member",
  product: "Product",
  location: "Location",
  property: "Property",
  opportunity: "Opportunity",
  proposal: "Proposal",
  job: "Job",
  invoice: "Invoice",
  commission_plan: "Commission Plan",
  field_definition: "Field Definition",
  business: "Business",
  account: "Account",
};

function formatEventTypes(eventTypes: string[]): string {
  if (eventTypes.includes("*")) return "All events";
  if (eventTypes.length === 0) return "None";
  if (eventTypes.length <= 3) return eventTypes.join(", ");
  return `${eventTypes.length} events`;
}

function formatRelativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── Topic Picker ─────────────────────────────────────────────────────────────

interface TopicPickerProps {
  selectedTopics: Set<string>;
  useWildcard: boolean;
  onToggleTopic: (topic: string) => void;
  onToggleWildcard: (checked: boolean) => void;
}

function TopicPicker({
  selectedTopics,
  useWildcard,
  onToggleTopic,
  onToggleWildcard,
}: TopicPickerProps) {
  return (
    <div className="space-y-3">
      <div
        className={cn(
          "rounded-md border p-3 transition-colors",
          useWildcard
            ? "border-[color:var(--color-border)] bg-[color:var(--color-elevated)]"
            : "border-[color:var(--color-border)]",
        )}
      >
        <label className="flex cursor-pointer items-start gap-3">
          <Checkbox
            checked={useWildcard}
            onCheckedChange={(checked: boolean) => onToggleWildcard(!!checked)}
            className="mt-0.5"
          />
          <div>
            <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
              All events
            </p>
            <p className="typo-body text-xs text-[color:var(--color-text-md)]">
              Receive every event type, including new ones added in the future.
            </p>
          </div>
        </label>
      </div>

      {!useWildcard && (
        <div className="space-y-3">
          <div className="rounded-md border border-[color:var(--color-border)] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
                  <th className="px-3 py-2 text-left typo-label text-[color:var(--color-text-md)] font-medium">
                    Entity
                  </th>
                  {ENTITY_EVENT_OPS.map((op) => (
                    <th
                      key={op}
                      className="px-3 py-2 text-center typo-label text-[color:var(--color-text-md)] font-medium capitalize"
                    >
                      {op}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {WEBHOOK_ENTITY_TYPES.map((entity, i) => (
                  <tr
                    key={entity}
                    className={cn(
                      "border-b border-[color:var(--color-border)] last:border-0",
                      i % 2 === 1 && "bg-[color:var(--color-surface)]",
                    )}
                  >
                    <td className="px-3 py-2 typo-body text-[color:var(--color-text-hi)]">
                      {ENTITY_LABELS[entity] ?? entity}
                    </td>
                    {ENTITY_EVENT_OPS.map((op) => {
                      const topic = `entity.${op}.${entity}`;
                      return (
                        <td key={op} className="px-3 py-2 text-center">
                          <Checkbox
                            checked={selectedTopics.has(topic)}
                            onCheckedChange={() => onToggleTopic(topic)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-md border border-[color:var(--color-border)] p-3">
            <p className="typo-label font-medium text-[color:var(--color-text-md)] mb-2">
              Import events
            </p>
            <div className="flex flex-wrap gap-2">
              {WEBHOOK_SYSTEM_EVENT_TYPES.map((topic) => (
                <label
                  key={topic}
                  className={cn(
                    "inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 typo-body text-xs transition-colors",
                    selectedTopics.has(topic)
                      ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/10 text-[color:var(--color-text-hi)]"
                      : "border-[color:var(--color-border)] text-[color:var(--color-text-md)] hover:bg-[color:var(--color-elevated)]",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selectedTopics.has(topic)}
                    onChange={() => onToggleTopic(topic)}
                    className="sr-only"
                  />
                  {topic}
                </label>
              ))}
            </div>
          </div>

          <p className="typo-body text-xs text-[color:var(--color-text-lo)]">
            Select specific event types to subscribe to.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Create Endpoint Sheet ────────────────────────────────────────────────────

interface CreateEndpointSheetProps {
  open: boolean;
  onClose: () => void;
}

function CreateEndpointSheet({ open, onClose }: CreateEndpointSheetProps) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [useWildcard, setUseWildcard] = useState(true);
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateWebhookEndpoint();

  function handleClose() {
    setName("");
    setUrl("");
    setUseWildcard(true);
    setSelectedTopics(new Set());
    setError(null);
    onClose();
  }

  function toggleTopic(topic: string) {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  }

  function toggleWildcard(checked: boolean) {
    setUseWildcard(checked);
    if (checked) setSelectedTopics(new Set());
  }

  async function handleSubmit() {
    setError(null);
    const eventTypes = useWildcard ? ["*"] : Array.from(selectedTopics);
    if (!useWildcard && eventTypes.length === 0) {
      setError("Select at least one event type or enable all events.");
      return;
    }
    setSubmitting(true);
    try {
      await createMutation.mutateAsync({ name, url, event_types: eventTypes });
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create endpoint",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Create webhook endpoint</SheetTitle>
        </SheetHeader>
        <form
          id="create-endpoint-form"
          className="flex-1 overflow-y-auto py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <SettingsField label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Production webhook"
            />
          </SettingsField>

          <SettingsField
            label="URL"
            description="HTTPS endpoint that will receive event payloads"
          >
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/webhooks/adva"
              type="url"
            />
          </SettingsField>

          <SettingsField label="Events">
            <TopicPicker
              selectedTopics={selectedTopics}
              useWildcard={useWildcard}
              onToggleTopic={toggleTopic}
              onToggleWildcard={toggleWildcard}
            />
          </SettingsField>

          {error && (
            <p className="typo-body text-sm text-destructive">{error}</p>
          )}
        </form>
        <FormSheetFooter
          onCancel={handleClose}
          submitLabel="Create endpoint"
          isSubmitting={submitting || !name.trim() || !url.trim()}
          formId="create-endpoint-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── Edit Endpoint Sheet ──────────────────────────────────────────────────────

interface EditEndpointSheetProps {
  endpoint: WebhookEndpoint | null;
  onClose: () => void;
}

function EditEndpointSheet({ endpoint, onClose }: EditEndpointSheetProps) {
  const [name, setName] = useState(endpoint?.name ?? "");
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [useWildcard, setUseWildcard] = useState(
    endpoint?.event_types?.includes("*") ?? true,
  );
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(
    new Set(endpoint?.event_types?.filter((t) => t !== "*") ?? []),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateWebhookEndpoint();

  useEffect(() => {
    if (endpoint) {
      setName(endpoint.name);
      setUrl(endpoint.url);
      const isWildcard = endpoint.event_types?.includes("*") ?? true;
      setUseWildcard(isWildcard);
      setSelectedTopics(
        new Set(isWildcard ? [] : (endpoint.event_types ?? [])),
      );
      setError(null);
    }
  }, [endpoint?.id]);

  function toggleTopic(topic: string) {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  }

  function toggleWildcard(checked: boolean) {
    setUseWildcard(checked);
    if (checked) setSelectedTopics(new Set());
  }

  async function handleSubmit() {
    if (!endpoint) return;
    setError(null);
    const eventTypes = useWildcard ? ["*"] : Array.from(selectedTopics);
    if (!useWildcard && eventTypes.length === 0) {
      setError("Select at least one event type or enable all events.");
      return;
    }
    setSubmitting(true);
    try {
      await updateMutation.mutateAsync({
        id: endpoint.id,
        name,
        url,
        event_types: eventTypes,
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update endpoint",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={!!endpoint} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Edit webhook endpoint</SheetTitle>
        </SheetHeader>
        <form
          id="edit-endpoint-form"
          className="flex-1 overflow-y-auto py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <SettingsField label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </SettingsField>

          <SettingsField
            label="URL"
            description="HTTPS endpoint that will receive event payloads"
          >
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              type="url"
            />
          </SettingsField>

          <SettingsField label="Events">
            <TopicPicker
              selectedTopics={selectedTopics}
              useWildcard={useWildcard}
              onToggleTopic={toggleTopic}
              onToggleWildcard={toggleWildcard}
            />
          </SettingsField>

          {error && (
            <p className="typo-body text-sm text-destructive">{error}</p>
          )}
        </form>
        <FormSheetFooter
          onCancel={onClose}
          submitLabel="Save changes"
          isSubmitting={submitting || !name.trim() || !url.trim()}
          formId="edit-endpoint-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function V2SettingsWebhooksPage() {
  const { data, isLoading } = useWebhookEndpoints();
  const deleteMutation = useDeleteWebhookEndpoint();
  const portalMutation = useWebhookPortalUrl();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] =
    useState<WebhookEndpoint | null>(null);
  const [deletingEndpoint, setDeletingEndpoint] =
    useState<WebhookEndpoint | null>(null);

  const endpoints = data?.webhook_endpoints ?? [];

  async function handleDeleteConfirm() {
    if (!deletingEndpoint) return;
    await deleteMutation.mutateAsync(deletingEndpoint.id);
    setDeletingEndpoint(null);
  }

  async function handleOpenPortal() {
    const result = await portalMutation.mutateAsync();
    window.location.href = result.redirect_url;
  }

  return (
    <SettingsPage
      title="Webhooks"
      subtitle="Receive real-time event notifications at your own HTTP endpoint."
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenPortal}
            disabled={portalMutation.isPending}
          >
            {portalMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            )}
            View delivery logs
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Create endpoint
          </Button>
        </div>
      }
    >
      <SettingsTable
        colSpan={5}
        isLoading={isLoading}
        isEmpty={endpoints.length === 0}
        emptyMessage="No webhook endpoints yet. Create one to start receiving events."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Name
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              URL
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Events
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden lg:table-cell">
              Created
            </TableHead>
            <TableHead className="w-20 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {endpoints.map((endpoint) => (
            <TableRow
              key={endpoint.id}
              className="h-9 border-b border-[color:var(--color-border)] last:border-0"
            >
              <TableCell className="px-4 py-1.5">
                <div className="flex items-center gap-2">
                  <Webhook className="h-3.5 w-3.5 text-[color:var(--color-text-lo)] shrink-0" />
                  <span className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    {endpoint.name}
                  </span>
                </div>
              </TableCell>
              <TableCell className="px-4 py-1.5 max-w-[200px]">
                <code className="block truncate rounded bg-[color:var(--color-surface)] px-2 py-0.5 typo-mono text-xs text-[color:var(--color-text-md)]">
                  {endpoint.url}
                </code>
              </TableCell>
              <TableCell className="px-4 py-1.5">
                <Badge
                  variant={
                    endpoint.event_types?.includes("*") ? "default" : "outline"
                  }
                  className={
                    endpoint.event_types?.includes("*")
                      ? "bg-[color:var(--color-elevated)] text-[color:var(--color-text-hi)] border-[color:var(--color-border)]"
                      : ""
                  }
                >
                  {formatEventTypes(endpoint.event_types ?? [])}
                </Badge>
              </TableCell>
              <TableCell className="px-4 py-1.5 hidden lg:table-cell typo-body text-xs text-[color:var(--color-text-lo)]">
                {formatRelativeDate(endpoint.created_at)}
              </TableCell>
              <TableCell className="px-4 py-1.5">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    title="Edit endpoint"
                    onClick={() => setEditingEndpoint(endpoint)}
                  >
                    <Pencil className="h-3.5 w-3.5 text-[color:var(--color-text-md)]" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 hover:bg-destructive/10"
                    title="Delete endpoint"
                    onClick={() => setDeletingEndpoint(endpoint)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SettingsTable>

      <CreateEndpointSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      <EditEndpointSheet
        endpoint={editingEndpoint}
        onClose={() => setEditingEndpoint(null)}
      />

      <ConfirmDialog
        open={!!deletingEndpoint}
        onOpenChange={(open) => !open && setDeletingEndpoint(null)}
        title="Delete webhook endpoint?"
        description={`Delete "${deletingEndpoint?.name}"? Events will stop being delivered to this URL immediately. This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />
    </SettingsPage>
  );
}
